import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { marked } from 'marked';
import { LEGAL_PAGES } from '../../data/portfolio';
import type { LegalDoc } from '../../data/portfolio';
import { applyPageMeta } from '../../lib/pageMeta';
import '../../styles/legal.css';

/**
 * Build Box legal pages (/fb-privacy, /fb-data-deletion). Content lives in
 * content/legal/*.md. scripts/inject-route-meta.js prerenders this exact
 * markup into the static route files so crawlers that never run JS (Meta's
 * app review) see the full policy text — keep class names in sync with it.
 */
const LegalPage: React.FC<{ doc: LegalDoc; alt: LegalDoc }> = ({ doc, alt }) => {
  // Same scroll unlock as usePlainPage(), inlined so the component stays
  // portable across trees that don't have that hook.
  useEffect(() => {
    document.documentElement.setAttribute('data-variant', 'scrollable');
    document.body.setAttribute('data-variant', 'scrollable');
    return () => {
      document.documentElement.removeAttribute('data-variant');
      document.body.removeAttribute('data-variant');
    };
  }, []);

  useEffect(() => {
    applyPageMeta({
      title: doc.title,
      description: doc.description,
      image: 'https://www.arshakil.com/og-home.png',
      path: `/${doc.slug}`,
    });
  }, [doc]);

  // First-party markdown from content/legal/*.md
  const html = marked.parse(doc.body) as string;

  return (
    <div className="legal">
      <main>
        <header className="legal-hero">
          <div className="legal-wrap">
            <div className="legal-label">Build Box</div>
            <h1 className="legal-h1">{doc.heading}</h1>
            <p className="legal-lede">{doc.lede}</p>
          </div>
        </header>

        <section className="legal-section" aria-label={doc.heading}>
          <div className="legal-wrap">
            <div className="legal-body" dangerouslySetInnerHTML={{ __html: html }} />
            <p className="legal-updated">Last updated: {doc.updated}</p>
          </div>
        </section>
      </main>

      <footer className="legal-footer">
        <div className="legal-wrap legal-footer-row">
          <Link className="legal-back" to="/">← arshakil.com</Link>
          <Link className="legal-alt" to={`/${alt.slug}`}>{alt.heading} →</Link>
        </div>
      </footer>
    </div>
  );
};

/** Route element: resolves the page by slug and cross-links its sibling. */
export function LegalRoute({ slug }: { slug: string }) {
  const doc = LEGAL_PAGES.find(p => p.slug === slug);
  if (!doc) return null;
  const alt = LEGAL_PAGES.find(p => p.slug !== slug) || doc;
  return <LegalPage doc={doc} alt={alt} />;
}

export default LegalPage;
